import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import Icon1 from "@assets/icons/Icon";
import { useState, useRef, useCallback, useEffect } from "react";
import { useDataStore } from "@store/user_org";
import { usePlanningStore, getCategories } from "@store/planningStore";
import { dossiersApi } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import { appointmentsApi } from "@services/api/appointments";
import { bookingsApi } from "@services/api/bookings";
import { searchDossiersFull, type ProjectHit } from "@services/algolia";
import { showNotification } from "context/notification.service";
import { formatTime } from "@utils/timeGrid";
import type { AppointmentCategory } from "types/garage/planning";

interface VehicleSuggestion {
  plate: string;
  marque: string;
  modele: string;
  vehicle_id?: string;
  dossier_id?: string;
  client_name?: string;
  image_url?: string;
}

interface QuickAppointmentModalProps {
  date: string;
  startHour: number;
  category: AppointmentCategory;
  onClose: () => void;
  onCreated: (ordreId: string) => void;
}

export default function QuickAppointmentModal({
  date,
  startHour,
  category,
  onClose,
  onCreated,
}: QuickAppointmentModalProps) {
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);

  const [clientName, setClientName] = useState("");
  const [plate, setPlate] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(category);
  const [saving, setSaving] = useState(false);

  // Dossier search
  const [dossierResults, setDossierResults] = useState<ProjectHit[]>([]);
  const [selectedDossierId, setSelectedDossierId] = useState<string | null>(null);
  const [searchingDossier, setSearchingDossier] = useState(false);

  // Phone lookup
  const [phoneLookupName, setPhoneLookupName] = useState<string | null>(null);

  // Vehicle lookup
  const [vehicleInfo, setVehicleInfo] = useState<{
    vehicle_id?: string;
    marque?: string;
    modele?: string;
    image_url?: string;
  } | null>(null);

  // Vehicle autocomplete suggestions
  const [vehicleSuggestions, setVehicleSuggestions] = useState<VehicleSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track if fields were auto-filled by plate lookup (so we can clear them)
  const [plateAutoFilled, setPlateAutoFilled] = useState(false);

  const handleSearchDossier = async (query: string) => {
    if (!orgData?.id || query.length < 2) {
      setDossierResults([]);
      return;
    }
    setSearchingDossier(true);
    try {
      const results = await searchDossiersFull(query, orgData.id);
      setDossierResults(results);
    } catch {
      setDossierResults([]);
    }
    setSearchingDossier(false);
  };

  const handleClientNameChange = (v: string) => {
    setClientName(v);
    setSelectedDossierId(null);
    setPhoneLookupName(null);
    handleSearchDossier(v);
  };

  const handleSelectDossier = (hit: ProjectHit) => {
    setSelectedDossierId(hit.value);
    setClientName(hit.label);
    setDossierResults([]);
  };

  // Search vehicles by prefix (debounced)
  const searchVehicles = useCallback(async (query: string) => {
    if (!orgData?.id) return;
    const raw = query.replace(/[^A-Za-z0-9]/g, "");
    if (raw.length < 2) {
      setVehicleSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const results = await bookingsApi.searchVehicles(raw, orgData.id);
      setVehicleSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch {
      setVehicleSuggestions([]);
      setShowSuggestions(false);
    }
  }, [orgData?.id]);

  // Select a vehicle from suggestions
  const handleSelectVehicle = (suggestion: VehicleSuggestion) => {
    setPlate(suggestion.plate);
    setVehicleInfo({
      vehicle_id: suggestion.vehicle_id,
      marque: suggestion.marque,
      modele: suggestion.modele,
      image_url: suggestion.image_url,
    });
    setVehicleSuggestions([]);
    setShowSuggestions(false);

    if (suggestion.dossier_id) {
      setSelectedDossierId(suggestion.dossier_id);
      if (suggestion.client_name) setClientName(suggestion.client_name);
      setPlateAutoFilled(true);
    }
  };

  // Plate lookup: triggers autocomplete + full lookup at 7 chars
  const handlePlateChange = async (v: string) => {
    setPlate(v);
    const raw = v.replace(/[^A-Za-z0-9]/g, "");

    // Clear previous auto-filled data when user changes plate
    if (plateAutoFilled) {
      setSelectedDossierId(null);
      setClientName("");
      setPhone("");
      setPhoneLookupName(null);
      setPlateAutoFilled(false);
    }
    setVehicleInfo(null);

    // Debounced autocomplete search (2+ chars, before full plate)
    if (raw.length >= 2 && raw.length < 7) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        searchVehicles(v);
      }, 300);
    } else {
      setVehicleSuggestions([]);
      setShowSuggestions(false);
    }

    // Full plate lookup at exactly 7 chars (existing behavior)
    if (raw.length === 7 && orgData?.id) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setVehicleSuggestions([]);
      setShowSuggestions(false);
      try {
        const result = await bookingsApi.lookupVehicle(raw, orgData.id);
        if (result.found) {
          setVehicleInfo({
            vehicle_id: result.vehicle_id,
            marque: result.marque,
            modele: result.modele,
            image_url: result.image_url,
          });
          if (result.dossier_id) {
            setSelectedDossierId(result.dossier_id);
            if (result.client_name) setClientName(result.client_name);
            if (result.client_phone) setPhone(result.client_phone);
            setPlateAutoFilled(true);
          }
        }
      } catch {
        // lookup failed
      }
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Phone lookup: triggers when phone reaches 10 digits
  const handlePhoneChange = async (v: string) => {
    setPhone(v);
    const digits = v.replace(/\D/g, "");
    if (digits.length === 10 && orgData?.id) {
      try {
        const result = await bookingsApi.lookupClient(digits, orgData.id);
        if (result.found) {
          const fullName = [result.prenom, result.nom].filter(Boolean).join(" ");
          if (fullName) setClientName(fullName);
          if (result.dossier_id) setSelectedDossierId(result.dossier_id);
          setPhoneLookupName(fullName || null);
        }
      } catch {
        // lookup failed silently
      }
    } else {
      setPhoneLookupName(null);
    }
  };

  const savingRef = useRef(false);

  const handleSave = async () => {
    if (!orgData?.id || !clientName.trim()) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    try {
      let dossierId = selectedDossierId;

      // Create dossier if no existing one selected
      if (!dossierId) {
        const now = { seconds: Math.floor(Date.now() / 1000) };
        const dossier = await dossiersApi.create({
          org_id: orgData.id,
          name: clientName.trim(),
          type: "particulier",
          tags: [],
          phone: phone.replace(/\s/g, "") || undefined,
          created_at: now,
        } as any);
        dossierId = dossier.id;
      }

      // Create ordre linked to dossier
      const now = { seconds: Math.floor(Date.now() / 1000) };
      const receptionDate = new Date(date + "T00:00:00");
      const hours = Math.floor(startHour);
      const minutes = Math.round((startHour - hours) * 60);
      receptionDate.setHours(hours, minutes, 0, 0);
      const receptionSeconds = Math.floor(receptionDate.getTime() / 1000);

      const ordre = await ordresApi.create({
        org_id: orgData.id,
        dossier_id: dossierId,
        name: clientName.trim(),
        num: `TEMP-${crypto.randomUUID().slice(0, 6).toUpperCase()}`,
        status: "a_preparer",
        immatriculation: plate || undefined,
        vehicle_id: vehicleInfo?.vehicle_id || undefined,
        assigned_to: userData?.id,
        reception_date: { seconds: receptionSeconds },
        tags: [],
        interventions: [],
        documents: [],
        created_at: now,
      } as any);

      // Backend _sync_appointments already creates the reception appointment from reception_date.
      // Update its category if user picked something other than default, then refetch.
      const defaultRecId = (orgData?.receptions?.[0]?.id) || "reception_1";
      if (selectedCategory !== defaultRecId) {
        try {
          const appts = await appointmentsApi.list({ org_id: orgData.id, date });
          const created = appts.find((a) => a.ordre_id === ordre.id);
          if (created) {
            await appointmentsApi.update(created.id, { category: selectedCategory });
          }
        } catch {
          // category update failed, not critical
        }
      }
      const planningState = usePlanningStore.getState();
      if (planningState._fetchedOrgId) {
        planningState.fetchAppointments(planningState._fetchedOrgId, true);
      }

      showNotification({ message: "Rendez-vous créé avec succès", type: "success" });
      onCreated(ordre.id);
      onClose();
    } catch {
      showNotification({ message: "Erreur lors de la création du rendez-vous", type: "error" });
      savingRef.current = false;
    }
    setSaving(false);
  };

  const dateObj = new Date(date + "T00:00:00");
  const dateLabel = dateObj.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });

  const allCategories = getCategories(orgData);
  const categoryOptions = allCategories.map((c) => ({
    value: c.id,
    label: c.label,
  }));

  return (
    <BaseModal
      title="Nouveau rendez-vous"
      startIcon={<Icon1 name="horloge" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Date/time info */}
        <div className="flex flex-row items-center gap-2 p-3 bg-indigo-50 rounded-lg">
          <Icon1 name="horloge" size={16} color="#6366F1" />
          <span className="text-h3 font-medium text-indigo-700">
            {dateLabel} — {formatTime(startHour)}
          </span>
        </div>

        {/* Immatriculation first — auto-lookup finds vehicle + dossier */}
        <div className="relative">
          <TextField
            label="Immatriculation"
            type="plate"
            value={plate}
            onChange={handlePlateChange}
            placeholder="AA-123-BB"
          />
          {/* Autocomplete suggestions dropdown */}
          {showSuggestions && vehicleSuggestions.length > 0 && !vehicleInfo && (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-200 overflow-y-auto">
              <div className="px-3 py-1.5 text-h4 text-neutral-400 border-b border-neutral-100">
                Véhicules correspondants
              </div>
              {vehicleSuggestions.map((s) => (
                <button
                  key={`${s.plate}-${s.vehicle_id || s.dossier_id}`}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors flex flex-row items-center gap-3"
                  onClick={() => handleSelectVehicle(s)}
                >
                  {s.image_url && (
                    <img
                      src={s.image_url}
                      alt={s.plate}
                      className="w-35 h-25 object-contain rounded flex-shrink-0"
                    />
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="text-h3 font-semibold text-neutral-800">{s.plate}</span>
                    <span className="text-h4 text-neutral-500">
                      {[s.marque, s.modele].filter(Boolean).join(" ")}
                    </span>
                    {s.client_name && (
                      <span className="text-h4 text-neutral-400 truncate">{s.client_name}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {vehicleInfo && (
            <div className="flex flex-row items-center gap-3 mt-1 p-2 bg-blue-50 rounded-lg border border-blue-200">
              {vehicleInfo.image_url && (
                <img
                  src={vehicleInfo.image_url}
                  alt="véhicule"
                  className="w-50 h-35 object-contain rounded"
                />
              )}
              <div className="flex flex-col">
                <span className="text-h3 font-medium text-blue-700">
                  {[vehicleInfo.marque, vehicleInfo.modele].filter(Boolean).join(" ")}
                </span>
                <span className="text-h4 text-blue-500">Véhicule trouvé</span>
              </div>
            </div>
          )}
        </div>

        {/* Phone with auto-lookup */}
        <div>
          <TextField
            label="Téléphone"
            type="phone"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="06 12 34 56 78"
          />
          {phoneLookupName && (
            <div className="flex flex-row items-center gap-2 mt-1 p-2 bg-green-50 rounded-lg border border-green-200">
              <Icon1 name="done2" size={14} color="#22C55E" />
              <span className="text-h3 font-medium text-green-700">
                Bonjour {phoneLookupName}
              </span>
            </div>
          )}
        </div>

        {/* Client name with dossier search */}
        <div className="relative">
          <TextField
            label="Nom du client"
            value={clientName}
            onChange={handleClientNameChange}
            placeholder="Rechercher ou créer..."
          />
          {dossierResults.length > 0 && !selectedDossierId && (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-200 overflow-y-auto">
              <div className="px-3 py-1.5 text-h4 text-neutral-400 border-b border-neutral-100">
                Dossiers existants
              </div>
              {dossierResults.map((hit) => (
                <button
                  key={hit.value}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors"
                  onClick={() => handleSelectDossier(hit)}
                >
                  <div className="text-h3 font-medium text-neutral-800">{hit.label}</div>
                  {hit.extra_label && (
                    <div className="text-h4 text-neutral-400">{hit.extra_label}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          {selectedDossierId && (
            <div className="flex flex-row items-center gap-1 mt-1">
              <Icon1 name="done2" size={12} color="#22C55E" />
              <span className="text-h4 text-green-600">Dossier existant sélectionné</span>
            </div>
          )}
        </div>

        {/* Category */}
        <DropDown
          label="Catégorie"
          options={categoryOptions}
          value={selectedCategory}
          onSelect={(v) => setSelectedCategory(v as AppointmentCategory)}
        />
      </div>

      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={saving ? "Création..." : "Créer le rendez-vous"}
          onClick={handleSave}
          disabled={saving || !clientName.trim()}
        />
      </div>
    </BaseModal>
  );
}
